import Box from "@mui/material/Box";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import * as React from "react";
import {useNavigate} from "react-router-dom";
import TextField from "@mui/material/TextField";
import {Button, ClickAwayListener, DialogContent, Popper, Tooltip, IconButton} from "@mui/material";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import SignInUserIntercomComponent, {UnauthenticatedUserIntercomComponent} from "../../components/IntercomComponent";
import {AuthContext} from "../../context/AuthContext";
import {NewLogoIconWithText} from "../NewHome/HomeIcons";
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckIcon from '@mui/icons-material/Check';
import HomeIcon from '@mui/icons-material/Home';
import {
    getPreferredAudioInputDeviceId,
    getPreferredAudioOutputDeviceId,
    getPreferredVideoInputDeviceId,
    setPreferredAudioInputDeviceId,
    setPreferredAudioOutputDeviceId,
    setPreferredVideoInputDeviceId,
} from "../../utils/mediaDevicePreferences";

// Placeholder SVG for when video is disabled
const VideoPlaceholder = () => (
    <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1A1A1A',
    }}>
        <svg width="120" height="120" viewBox="0 0 552 505" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M276 282.5C352.5 282.5 414.545 226.682 414.545 150C414.545 73.3182 352.5 17.5 276 17.5C199.5 17.5 137.455 73.3182 137.455 150C137.455 226.682 199.5 282.5 276 282.5Z"
                fill="white"
                fillOpacity="0.25"
            />
            <path
                d="M168.236 305.872C178.797 301.83 190.839 303.137 201.389 307.508C224.449 318.703 250.182 325 276 325C301.818 325 327.551 318.703 350.611 307.508C361.161 303.137 373.203 301.83 383.764 305.872C453.08 337.373 503.353 398.65 514.769 471.97C517.502 489.059 501.742 505 483.5 505H69.5C51.2581 505 35.4975 489.059 38.2313 471.97C49.6473 398.65 99.9201 337.373 168.236 305.872Z"
                fill="white"
                fillOpacity="0.25"
            />
        </svg>
    </Box>
);

const stopStreamTracks = (stream) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
};

const isPermissionDeniedError = (error) => {
    const name = error?.name || '';
    return name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError';
};

const isMissingDeviceError = (error) => {
    const name = error?.name || '';
    return name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError';
};

const permissionStateFromError = (error) => {
    if (isPermissionDeniedError(error)) return 'denied';
    if (isMissingDeviceError(error)) return 'unavailable';
    return 'error';
};

const permissionStatusLabel = (state) => {
    if (state === 'granted') return 'Ready';
    if (state === 'denied') return 'Blocked';
    if (state === 'unavailable') return 'No device found';
    if (state === 'prompt') return 'Needs permission';
    if (state === 'error') return 'Unavailable';
    return 'Checking';
};

const waitForMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pickAvailableDeviceId = (devices, ...candidateIds) => {
    const normalizedIds = candidateIds
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean);

    for (const candidateId of normalizedIds) {
        if (devices.some((device) => device.deviceId === candidateId)) {
            return candidateId;
        }
    }

    return '';
};

// Device Pill Button Component
const DevicePill = ({ icon: Icon, label, deviceName, enabled, onToggle, onDeviceSelect, devices, selectedDeviceId, kind }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const pillRef = useRef(null);
    const open = Boolean(anchorEl);

    const handlePillClick = (e) => {
        e.stopPropagation();
        setAnchorEl(anchorEl ? null : pillRef.current);
    };

    const handleToggle = (e) => {
        e.stopPropagation();
        onToggle();
    };

    const handleDeviceClick = (deviceId) => {
        onDeviceSelect(deviceId);
        setAnchorEl(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const truncatedName = deviceName && deviceName.length > 20 
        ? deviceName.substring(0, 20) + '...' 
        : deviceName || 'Default';

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box sx={{ position: 'relative', width: '100%' }}>
                <Box
                    ref={pillRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 40,
                        borderRadius: '8px',
                        backgroundColor: '#EDEDED',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                            backgroundColor: '#E3E3E3',
                        },
                    }}
                >
                    {/* Toggle button */}
                    <Box
                        onClick={handleToggle}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: '100%',
                            borderRight: '1px solid rgba(0,0,0,0.08)',
                            color: enabled ? '#1A1A1A' : '#9CA3AF',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.05)',
                            },
                        }}
                    >
                        <Icon sx={{ fontSize: 20 }} />
                    </Box>

                    {/* Device selector */}
                    <Box
                        onClick={handlePillClick}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            height: '100%',
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                color: enabled ? '#1A1A1A' : '#9CA3AF',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontFamily: 'Inter, sans-serif',
                            }}
                        >
                            {truncatedName}
                        </Typography>
                        <KeyboardArrowUpIcon 
                            sx={{ 
                                fontSize: 18, 
                                color: 'var(--app-text-muted)',
                                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            }} 
                        />
                    </Box>
                </Box>

                {/* Drop-up panel */}
                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="top-start"
                    style={{ zIndex: 1300 }}
                    modifiers={[
                        { name: 'offset', options: { offset: [0, 8] } },
                    ]}
                >
                    <Box
                        sx={{
                            backgroundColor: 'var(--app-bg-surface)',
                            borderRadius: '12px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            minWidth: pillRef.current?.offsetWidth || 200,
                            maxWidth: 320,
                            py: 1,
                            overflow: 'hidden',
                        }}
                    >
                        {devices.map((device) => (
                            <Box
                                key={device.deviceId}
                                onClick={() => handleDeviceClick(device.deviceId)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1.25,
                                    cursor: 'pointer',
                                    backgroundColor: selectedDeviceId === device.deviceId ? 'rgba(43,107,253,0.06)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'var(--app-bg-subtle)',
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: 400,
                                        color: 'var(--app-text-primary)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontFamily: 'Inter, sans-serif',
                                    }}
                                >
                                    {device.label || `${kind === 'audioinput' ? 'Microphone' : 'Camera'} ${devices.indexOf(device) + 1}`}
                                </Typography>
                                {selectedDeviceId === device.deviceId && (
                                    <CheckIcon sx={{ fontSize: 16, color: '#2B6BFD', ml: 1, flexShrink: 0 }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

// Output device selector - smaller, secondary style
const OutputSelector = ({ deviceName, onDeviceSelect, devices, selectedDeviceId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const selectorRef = useRef(null);
    const open = Boolean(anchorEl);

    const handleClick = (e) => {
        e.stopPropagation();
        setAnchorEl(anchorEl ? null : selectorRef.current);
    };

    const handleDeviceClick = (deviceId) => {
        onDeviceSelect(deviceId);
        setAnchorEl(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const truncatedName = deviceName && deviceName.length > 30 
        ? deviceName.substring(0, 30) + '...' 
        : deviceName || 'System Default';

    // Add "System Default" option to devices list
    const allDevices = [
        { deviceId: 'default', label: 'System Default' },
        ...devices.filter(d => d.deviceId !== 'default'),
    ];

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box sx={{ position: 'relative', px: 2, py: 1.5 }}>
                <Box
                    ref={selectorRef}
                    onClick={handleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover': {
                            '& .output-label': {
                                color: '#4B5563',
                            },
                        },
                    }}
                >
                    <VolumeUpIcon sx={{ fontSize: 16, color: 'var(--app-text-subtle)' }} />
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'var(--app-text-subtle)',
                        }}
                    >
                        Output:
                    </Typography>
                    <Typography
                        className="output-label"
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'var(--app-text-muted)',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {truncatedName}
                    </Typography>
                    <KeyboardArrowUpIcon 
                        sx={{ 
                            fontSize: 16, 
                            color: 'var(--app-text-subtle)',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }} 
                    />
                </Box>

                {/* Drop-up panel */}
                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="top-start"
                    style={{ zIndex: 1300 }}
                    modifiers={[
                        { name: 'offset', options: { offset: [0, 8] } },
                    ]}
                >
                    <Box
                        sx={{
                            backgroundColor: 'var(--app-bg-surface)',
                            borderRadius: '12px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            minWidth: selectorRef.current?.offsetWidth || 200,
                            maxWidth: 360,
                            py: 1,
                            overflow: 'hidden',
                        }}
                    >
                        {allDevices.map((device) => (
                            <Box
                                key={device.deviceId}
                                onClick={() => handleDeviceClick(device.deviceId)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1,
                                    cursor: 'pointer',
                                    backgroundColor: selectedDeviceId === device.deviceId ? 'rgba(43,107,253,0.06)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'var(--app-bg-subtle)',
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        color: 'var(--app-text-primary)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontFamily: 'Inter, sans-serif',
                                    }}
                                >
                                    {device.label || `Speaker ${allDevices.indexOf(device)}`}
                                </Typography>
                                {selectedDeviceId === device.deviceId && (
                                    <CheckIcon sx={{ fontSize: 14, color: '#2B6BFD', ml: 1, flexShrink: 0 }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

// Waiting card shown below preview when guest is waiting for admission
const WaitingCard = ({ guestName, onCancel, status = 'pending' }) => {
    const isDenied = status === 'denied';

    if (isDenied) {
        return (
            <Box sx={{
                width: '100%',
                backgroundColor: 'var(--app-bg-surface)',
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
            }}>
                <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: '#FEE2E2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6l12 12" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </Box>
                <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'var(--app-text-primary)',
                    mb: 0.5,
                }}>
                    Unable to join
                </Typography>
                <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: 'var(--app-text-muted)',
                    mb: 2.5,
                }}>
                    The host didn't admit you to this session.
                </Typography>
                <Button
                    onClick={onCancel}
                    fullWidth
                    sx={{
                        height: 44,
                        borderRadius: '10px',
                        backgroundColor: '#000000',
                        color: '#FFFFFF',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#1A1A1A' },
                    }}
                >
                    Try again
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            backgroundColor: 'var(--app-bg-surface)',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
        }}>
            {/* Animated bell icon */}
            <Box sx={{
                mb: 2,
                '& svg': {
                    animation: 'bellRing 2s ease-in-out infinite',
                },
                '@keyframes bellRing': {
                    '0%, 100%': { transform: 'rotate(0deg)' },
                    '10%': { transform: 'rotate(14deg)' },
                    '20%': { transform: 'rotate(-14deg)' },
                    '30%': { transform: 'rotate(10deg)' },
                    '40%': { transform: 'rotate(-10deg)' },
                    '50%': { transform: 'rotate(6deg)' },
                    '60%': { transform: 'rotate(-6deg)' },
                    '70%, 90%': { transform: 'rotate(0deg)' },
                },
            }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
            </Box>

            <Typography sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--app-text-primary)',
                mb: 0.5,
            }}>
                Waiting to be admitted
            </Typography>
            
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 2.5,
            }}>
                <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: 'var(--app-text-muted)',
                }}>
                    The host will let you in soon
                </Typography>
                {/* Loading dots */}
                <Box sx={{ display: 'flex', gap: '3px', ml: 0.5 }}>
                    {[0, 1, 2].map((i) => (
                        <Box
                            key={i}
                            sx={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: '#9CA3AF',
                                animation: 'dotBounce 1.4s ease-in-out infinite',
                                animationDelay: `${i * 0.16}s`,
                                '@keyframes dotBounce': {
                                    '0%, 80%, 100%': { opacity: 0.3 },
                                    '40%': { opacity: 1 },
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* Guest info */}
            <Box sx={{
                width: '100%',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                p: 1.5,
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
            }}>
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: '#E5D9C8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Typography sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'var(--app-text-primary)',
                    }}>
                        {guestName?.charAt(0)?.toUpperCase() || 'G'}
                    </Typography>
                </Box>
                <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#374151',
                }}>
                    {guestName}
                </Typography>
            </Box>

            <Button
                onClick={onCancel}
                fullWidth
                sx={{
                    height: 44,
                    borderRadius: '10px',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--app-border-subtle)',
                    color: '#374151',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: '#F9FAFB',
                        borderColor: 'var(--app-border-muted)',
                    },
                }}
            >
                Leave queue
            </Button>
        </Box>
    );
};

const NewLobby = ({userFriendlyStudioName, onSubmit, isHost, errorMessage, isSmallScreen, initialTitle, isWaiting = false, waitingStatus = 'pending', waitingGuestName = '', onCancelWaiting}) => {
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    const videoRef = useRef(null);

    // Preview stream
    const [previewStream, setPreviewStream] = useState(null);

    // Audio level indicator refs
    const audioIndicatorRef = useRef(null);
    const audioAnalyserRef = useRef(null);
    const audioLevelRef = useRef(0);
    const audioAnimationRef = useRef(null);

    // Form state
    const [usingHeadphones, setUsingHeadphones] = useState(false);
    const [recordingTitle, setRecordingTitle] = useState(initialTitle || "");
    // For host, default to user's display name
    const defaultUsername = isHost ? (authContext.getUserDisplayName?.() || '') : '';
    const [username, setUsername] = useState(defaultUsername);

    // Device lists
    const [audioDevices, setAudioDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);
    const [outputDevices, setOutputDevices] = useState([]);

    // Device settings
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [audioDeviceId, setAudioDeviceId] = useState(() => getPreferredAudioInputDeviceId());
    const [videoDeviceId, setVideoDeviceId] = useState(() => getPreferredVideoInputDeviceId());
    const [outputDeviceId, setOutputDeviceId] = useState(() => getPreferredAudioOutputDeviceId() || 'default');
    const [audioPermissionState, setAudioPermissionState] = useState('unknown');
    const [videoPermissionState, setVideoPermissionState] = useState('unknown');
    const [permissionHint, setPermissionHint] = useState('');
    const [requestingPermissions, setRequestingPermissions] = useState(false);

    useEffect(() => {
        if (audioDeviceId) {
            setPreferredAudioInputDeviceId(audioDeviceId);
        }
    }, [audioDeviceId]);

    useEffect(() => {
        if (videoDeviceId) {
            setPreferredVideoInputDeviceId(videoDeviceId);
        }
    }, [videoDeviceId]);

    useEffect(() => {
        if (outputDeviceId) {
            setPreferredAudioOutputDeviceId(outputDeviceId);
        }
    }, [outputDeviceId]);

    // Set page title
    useEffect(() => {
        document.title = recordingTitle || "Recording Studio";
    }, [recordingTitle]);

    const enumerateDevicesAndApplyDefaults = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter((d) => d.kind === 'audioinput');
            const videoInputs = devices.filter((d) => d.kind === 'videoinput');
            const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

            setAudioDevices(audioInputs);
            setVideoDevices(videoInputs);
            setOutputDevices(audioOutputs);

            const preferredAudioInputDeviceId = getPreferredAudioInputDeviceId();
            const preferredVideoInputDeviceId = getPreferredVideoInputDeviceId();

            setAudioDeviceId((previous) => pickAvailableDeviceId(
                audioInputs,
                previous,
                preferredAudioInputDeviceId,
                audioInputs[0]?.deviceId || '',
            ));
            setVideoDeviceId((previous) => pickAvailableDeviceId(
                videoInputs,
                previous,
                preferredVideoInputDeviceId,
                videoInputs[0]?.deviceId || '',
            ));

            const preferredAudioOutputDeviceId = getPreferredAudioOutputDeviceId();
            setOutputDeviceId((previous) => {
                const normalizedPrevious = typeof previous === 'string' ? previous.trim() : '';
                if (normalizedPrevious === 'default') return 'default';
                if (preferredAudioOutputDeviceId === 'default') return 'default';

                const resolvedOutputDeviceId = pickAvailableDeviceId(
                    audioOutputs,
                    previous,
                    preferredAudioOutputDeviceId,
                    audioOutputs[0]?.deviceId || '',
                );

                return resolvedOutputDeviceId || 'default';
            });
        } catch (err) {
            console.error('Error enumerating devices:', err);
        }
    }, []);

    const refreshPermissionStatesFromBrowser = useCallback(async () => {
        if (!navigator?.mediaDevices?.getUserMedia) {
            setPermissionHint('This browser does not support camera and microphone capture.');
            setAudioPermissionState('error');
            setVideoPermissionState('error');
            return;
        }

        const requestStartedAt = Date.now();
        setRequestingPermissions(true);

        try {
            let nextAudioState = 'unknown';
            let nextVideoState = 'unknown';
            let nextPermissionHint = '';

            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
                stopStreamTracks(stream);
                nextAudioState = 'granted';
                nextVideoState = 'granted';
            } catch (combinedError) {
                if (isPermissionDeniedError(combinedError)) {
                    nextPermissionHint = 'Allow access in your browser settings to continue.';
                } else if (isMissingDeviceError(combinedError)) {
                    nextPermissionHint = 'No camera or microphone was found. Connect a device and try again.';
                } else {
                    nextPermissionHint = 'Could not access your camera or microphone. Try again or check your browser settings.';
                }

                try {
                    const audioOnlyStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stopStreamTracks(audioOnlyStream);
                    nextAudioState = 'granted';
                } catch (audioError) {
                    nextAudioState = permissionStateFromError(audioError);
                }

                try {
                    const videoOnlyStream = await navigator.mediaDevices.getUserMedia({ video: true });
                    stopStreamTracks(videoOnlyStream);
                    nextVideoState = 'granted';
                } catch (videoError) {
                    nextVideoState = permissionStateFromError(videoError);
                }

                if (nextAudioState === 'unknown') {
                    nextAudioState = permissionStateFromError(combinedError);
                }
                if (nextVideoState === 'unknown') {
                    nextVideoState = permissionStateFromError(combinedError);
                }
            }

            if (navigator.permissions?.query) {
                try {
                    const microphonePermission = await navigator.permissions.query({ name: 'microphone' });
                    if (microphonePermission.state && nextAudioState !== 'granted') {
                        nextAudioState = microphonePermission.state;
                    }
                } catch {}

                try {
                    const cameraPermission = await navigator.permissions.query({ name: 'camera' });
                    if (cameraPermission.state && nextVideoState !== 'granted') {
                        nextVideoState = cameraPermission.state;
                    }
                } catch {}
            }

            if (nextAudioState === 'denied' || nextVideoState === 'denied') {
                nextPermissionHint = 'Allow access in your browser settings to continue.';
            }

            setAudioPermissionState(nextAudioState);
            setVideoPermissionState(nextVideoState);
            setPermissionHint(nextPermissionHint);
            await enumerateDevicesAndApplyDefaults();
        } finally {
            const elapsed = Date.now() - requestStartedAt;
            if (elapsed < 1000) {
                await waitForMs(1000 - elapsed);
            }
            setRequestingPermissions(false);
        }
    }, [enumerateDevicesAndApplyDefaults]);

    // Initialize permissions + devices on mount
    useEffect(() => {
        refreshPermissionStatesFromBrowser();
    }, [refreshPermissionStatesFromBrowser]);

    // Update preview stream when devices or enabled state changes
    useEffect(() => {
        let cancelled = false;

        const updatePreview = async () => {
            // Stop existing stream
            if (previewStream) {
                previewStream.getTracks().forEach(track => track.stop());
            }

            // If nothing enabled, clear stream
            if (!audioEnabled && !videoEnabled) {
                setPreviewStream(null);
                return;
            }

            try {
                const constraints = {};
                
                if (audioEnabled && audioDeviceId) {
                    constraints.audio = { deviceId: { exact: audioDeviceId } };
                } else if (audioEnabled) {
                    constraints.audio = true;
                }

                if (videoEnabled && videoDeviceId) {
                    constraints.video = { 
                        deviceId: { exact: videoDeviceId },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    };
                } else if (videoEnabled) {
                    constraints.video = {
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    };
                }

                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                
                if (!cancelled) {
                    setPreviewStream(stream);
                    if (audioEnabled) {
                        setAudioPermissionState('granted');
                    }
                    if (videoEnabled) {
                        setVideoPermissionState('granted');
                    }
                    setPermissionHint('');
                } else {
                    stream.getTracks().forEach(track => track.stop());
                }
            } catch (err) {
                console.error('Error getting preview stream:', err);
                if (audioEnabled) {
                    setAudioPermissionState(permissionStateFromError(err));
                }
                if (videoEnabled) {
                    setVideoPermissionState(permissionStateFromError(err));
                }

                if (isPermissionDeniedError(err)) {
                    setPermissionHint('Allow access in your browser settings to continue.');
                } else if (isMissingDeviceError(err)) {
                    setPermissionHint('A required camera or microphone is missing. Connect a device and try again.');
                } else {
                    setPermissionHint('Preview is unavailable right now. Try requesting access again.');
                }
            }
        };

        updatePreview();

        return () => {
            cancelled = true;
        };
    }, [audioEnabled, videoEnabled, audioDeviceId, videoDeviceId]);

    // Attach stream to video element
    useEffect(() => {
        if (videoRef.current && previewStream) {
            videoRef.current.srcObject = previewStream;
        }
    }, [previewStream]);

    // Cleanup stream on unmount
    useEffect(() => {
        return () => {
            if (previewStream) {
                previewStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [previewStream]);

    // Audio level indicator
    useEffect(() => {
        if (!previewStream || !audioEnabled) {
            // Cleanup
            if (audioAnimationRef.current) {
                cancelAnimationFrame(audioAnimationRef.current);
                audioAnimationRef.current = null;
            }
            if (audioIndicatorRef.current) {
                audioIndicatorRef.current.style.height = '10%';
            }
            return;
        }

        const audioTrack = previewStream.getAudioTracks()[0];
        if (!audioTrack) return;

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        audioAnalyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(previewStream);
        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateLevel = () => {
            if (!audioAnalyserRef.current) return;
            
            audioAnimationRef.current = requestAnimationFrame(updateLevel);
            analyser.getByteTimeDomainData(dataArray);

            // Calculate RMS level
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                const value = (dataArray[i] - 128) / 128;
                sum += value * value;
            }
            const rms = Math.sqrt(sum / bufferLength);
            
            // Smooth the level
            const smoothing = 0.3;
            audioLevelRef.current = audioLevelRef.current * (1 - smoothing) + rms * smoothing;

            // Update the indicator element
            if (audioIndicatorRef.current) {
                const level = Math.min(1, audioLevelRef.current * 3);
                audioIndicatorRef.current.style.height = `${10 + level * 90}%`;
            }
        };

        updateLevel();

        return () => {
            if (audioAnimationRef.current) {
                cancelAnimationFrame(audioAnimationRef.current);
            }
            source.disconnect();
            analyser.disconnect();
            audioContext.close();
            audioAnalyserRef.current = null;
        };
    }, [previewStream, audioEnabled]);

    // Get device name by ID
    const getAudioDeviceName = () => {
        const device = audioDevices.find(d => d.deviceId === audioDeviceId);
        return device?.label || (audioDevices[0]?.label) || 'Microphone';
    };

    const getVideoDeviceName = () => {
        const device = videoDevices.find(d => d.deviceId === videoDeviceId);
        return device?.label || (videoDevices[0]?.label) || 'Camera';
    };

    const getOutputDeviceName = () => {
        if (outputDeviceId === 'default') {
            return 'System Default';
        }
        const device = outputDevices.find(d => d.deviceId === outputDeviceId);
        return device?.label || 'System Default';
    };

    const buildDefaultRecordingTitle = () => {
        const now = new Date();
        const rounded = new Date(now);
        if (rounded.getMinutes() >= 30) {
            rounded.setHours(rounded.getHours() + 1);
        }
        rounded.setMinutes(0, 0, 0);

        const datePart = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
        }).format(rounded);
        const timePart = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
        }).format(rounded);
        return `Recording – ${datePart}, ${timePart}`;
    };

    const handleEnterStudio = () => {
        const trimmedTitle = recordingTitle.trim();
        const finalTitle = isHost
            ? (trimmedTitle === '' ? buildDefaultRecordingTitle() : trimmedTitle)
            : trimmedTitle;
        onSubmit({
            title: finalTitle,
            username,
            audioEnabled,
            videoEnabled,
            audioDeviceId,
            videoDeviceId,
            outputDeviceId,
            usingHeadphones,
        });
    };

    const canEnter = username.trim() !== '';
    const audioNeedsAttention = audioEnabled && audioPermissionState !== 'granted';
    const videoNeedsAttention = videoEnabled && videoPermissionState !== 'granted';
    const shouldShowPermissionCard = audioNeedsAttention || videoNeedsAttention || permissionHint !== '';
    const requiresBrowserSettings = (audioNeedsAttention && audioPermissionState === 'denied')
        || (videoNeedsAttention && videoPermissionState === 'denied');

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100vw',
            backgroundColor: 'var(--app-bg-subtle)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            px: 2,
            position: 'relative',
        }}>
            {isHost ? (
                <SignInUserIntercomComponent user={authContext.getUser()} />
            ) : (
                <UnauthenticatedUserIntercomComponent />
            )}

            {/* Back to projects button - host only */}
            {isHost && (
                <Box sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                }}>
                    <Tooltip title="Back to projects">
                        <IconButton 
                            onClick={() => navigate("/dashboard")}
                            sx={{
                                color: '#5a5a5a',
                                fontSize: '1.2rem',
                                '& .MuiSvgIcon-root': { fontSize: '1.4rem' },
                            }}
                        >
                            <HomeIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Main content column */}
            <Box sx={{
                width: '100%',
                maxWidth: 'clamp(420px, 40vw, 520px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'clamp(12px, 2vh, 24px)',
            }}>
                {/* Logo (guest only) */}
                {!isHost && (
                    <Box
                        onClick={() => navigate("/dashboard")}
                        sx={{
                            cursor: 'pointer',
                            mb: 2,
                        }}
                    >
                        <NewLogoIconWithText />
                    </Box>
                )}

                {/* Header + Recording name */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 'clamp(22px, 2.5vw, 28px)',
                            fontWeight: 600,
                            color: 'var(--app-text-primary)',
                            lineHeight: 1.3,
                        }}>
                            {isHost ? 'New Recording' : 'Join Recording'}
                        </Typography>
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: 'var(--app-text-muted)',
                            mt: 1,
                        }}>
                            {isHost 
                                ? "You're about to start a recording" 
                                : "You're joining a recording session"}
                        </Typography>
                    </Box>

                    {/* Recording name input - host only */}
                    {isHost && (
                        <Box>
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: 'var(--app-text-muted)',
                                mb: 0.75,
                            }}>
                                Recording name
                            </Typography>
                            <TextField
                                fullWidth
                                value={recordingTitle}
                                onChange={(e) => setRecordingTitle(e.target.value)}
                                onFocus={() => {
                                    if (recordingTitle.trim() === '') {
                                        setRecordingTitle(buildDefaultRecordingTitle());
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && canEnter) {
                                        handleEnterStudio();
                                    }
                                }}
                                placeholder="e.g. Client Interview – Jan 25"
                                InputProps={{
                                    sx: {
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        color: 'var(--app-text-primary)',
                                        backgroundColor: 'var(--app-bg-surface)',
                                        borderRadius: '8px',
                                        height: 46,
                                        '& fieldset': {
                                            borderColor: '#E8E8E8',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'var(--app-border-muted)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#9CA3AF',
                                            borderWidth: 1,
                                        },
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>

                {/* Video Preview Card */}
                <Box sx={{
                    width: '100%',
                    backgroundColor: 'var(--app-bg-surface)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                }}>
                    {/* Video container with 16:9 aspect ratio */}
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        backgroundColor: '#1A1A1A',
                        overflow: 'hidden',
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}>
                            {videoEnabled && previewStream ? (
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transform: 'scaleX(-1)',
                                        display: 'block',
                                        backgroundColor: '#1A1A1A',
                                    }}
                                />
                            ) : (
                                <VideoPlaceholder />
                            )}

                            {/* Audio level indicator - thin vertical bar */}
                            {audioEnabled && previewStream && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: 16,
                                        bottom: 16,
                                        width: 4,
                                        height: '30%',
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <Box
                                        ref={audioIndicatorRef}
                                        sx={{
                                            width: '100%',
                                            height: '10%',
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            borderRadius: 2,
                                            transition: 'height 0.05s ease-out',
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Username overlay */}
                            {username && (
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: 12,
                                    left: 12,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    borderRadius: '6px',
                                    px: 1,
                                    py: 0.5,
                                }}>
                                    <Typography sx={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#FFFFFF',
                                    }}>
                                        {username}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Device controls below video */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#FAFAFA',
                        borderTop: '1px solid #F0F0F0',
                    }}>
                        {/* Mic and Camera row */}
                        <Box sx={{
                            display: 'flex',
                            gap: 1.5,
                            p: 2,
                            pb: 1.5,
                        }}>
                            <Box sx={{ flex: 1 }}>
                                <DevicePill
                                    icon={audioEnabled ? MicIcon : MicOffIcon}
                                    deviceName={getAudioDeviceName()}
                                    enabled={audioEnabled}
                                    onToggle={() => setAudioEnabled(!audioEnabled)}
                                    onDeviceSelect={(id) => setAudioDeviceId(id)}
                                    devices={audioDevices}
                                    selectedDeviceId={audioDeviceId}
                                    kind="audioinput"
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <DevicePill
                                    icon={videoEnabled ? VideocamIcon : VideocamOffIcon}
                                    deviceName={getVideoDeviceName()}
                                    enabled={videoEnabled}
                                    onToggle={() => setVideoEnabled(!videoEnabled)}
                                    onDeviceSelect={(id) => setVideoDeviceId(id)}
                                    devices={videoDevices}
                                    selectedDeviceId={videoDeviceId}
                                    kind="videoinput"
                                />
                            </Box>
                        </Box>

                        {/* Divider */}
                        <Box sx={{ 
                            height: '1px', 
                            backgroundColor: '#E8E8E8', 
                            mx: 2,
                        }} />

                        {/* Output device row */}
                        <OutputSelector
                            deviceName={getOutputDeviceName()}
                            onDeviceSelect={(id) => setOutputDeviceId(id)}
                            devices={outputDevices}
                            selectedDeviceId={outputDeviceId}
                        />

                        {shouldShowPermissionCard && (
                            <Box sx={{
                                mx: 2,
                                mb: 2,
                                mt: 0.5,
                                p: 1.5,
                                borderRadius: '10px',
                                border: '1px solid var(--app-border-subtle)',
                                backgroundColor: 'var(--app-bg-surface)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}>
                                <Typography sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: 'var(--app-text-primary)',
                                }}>
                                    Camera and microphone access
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {audioNeedsAttention && (
                                        <Typography sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: 'var(--app-text-muted)',
                                        }}>
                                            Microphone: {permissionStatusLabel(audioPermissionState)}
                                        </Typography>
                                    )}
                                    {videoNeedsAttention && (
                                        <Typography sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: 'var(--app-text-muted)',
                                        }}>
                                            Camera: {permissionStatusLabel(videoPermissionState)}
                                        </Typography>
                                    )}
                                </Box>

                                {permissionHint && (
                                    <Typography sx={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: '11px',
                                        fontWeight: 400,
                                        color: 'var(--app-text-muted)',
                                        lineHeight: 1.45,
                                    }}>
                                        {requiresBrowserSettings ? (
                                            <>
                                                Allow access in your{' '}
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: 'var(--app-text-primary)',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    browser settings
                                                </Box>{' '}
                                                to continue.
                                            </>
                                        ) : (
                                            permissionHint
                                        )}
                                    </Typography>
                                )}

                                {!requiresBrowserSettings ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <Button
                                            onClick={refreshPermissionStatesFromBrowser}
                                            disabled={requestingPermissions}
                                            sx={{
                                                height: 32,
                                                borderRadius: '8px',
                                                px: 1.5,
                                                backgroundColor: '#000000',
                                                color: '#FFFFFF',
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: '#1A1A1A',
                                                },
                                                '&:disabled': {
                                                    backgroundColor: '#000000',
                                                    color: '#FFFFFF',
                                                    opacity: 0.35,
                                                },
                                            }}
                                        >
                                            {requestingPermissions ? 'Requesting access…' : 'Request access'}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Typography sx={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: '11px',
                                        fontWeight: 400,
                                        color: 'var(--app-text-muted)',
                                        lineHeight: 1.45,
                                    }}>
                                        After changing browser permissions, reload this page.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Form Section - or Waiting Card if waiting */}
                {isWaiting ? (
                    <WaitingCard 
                        guestName={waitingGuestName} 
                        onCancel={onCancelWaiting} 
                        status={waitingStatus} 
                    />
                ) : (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                    {/* Username input */}
                    <Box>
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'var(--app-text-muted)',
                            mb: 0.75,
                        }}>
                            Your name
                        </Typography>
                        <TextField
                            fullWidth
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canEnter) {
                                    handleEnterStudio();
                                }
                            }}
                            placeholder="How you'll appear to others"
                            InputProps={{
                                sx: {
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: 'var(--app-text-primary)',
                                    backgroundColor: 'var(--app-bg-surface)',
                                    borderRadius: '8px',
                                    height: 48,
                                    '& fieldset': {
                                        borderColor: '#E8E8E8',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'var(--app-border-muted)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#9CA3AF',
                                        borderWidth: 1,
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Headphone toggle */}
                    <Box>
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'var(--app-text-muted)',
                            mb: 1,
                        }}>
                            Audio setup
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box
                                onClick={() => setUsingHeadphones(true)}
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 40,
                                    borderRadius: '8px',
                                    backgroundColor: usingHeadphones ? '#000000' : '#EDEDED',
                                    color: usingHeadphones ? '#FFFFFF' : '#4B5563',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        backgroundColor: usingHeadphones ? '#1A1A1A' : '#E3E3E3',
                                    },
                                }}
                            >
                                <Typography sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                }}>
                                    Using headphones
                                </Typography>
                            </Box>
                            <Box
                                onClick={() => setUsingHeadphones(false)}
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 40,
                                    borderRadius: '8px',
                                    backgroundColor: !usingHeadphones ? '#000000' : '#EDEDED',
                                    color: !usingHeadphones ? '#FFFFFF' : '#4B5563',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        backgroundColor: !usingHeadphones ? '#1A1A1A' : '#E3E3E3',
                                    },
                                }}
                            >
                                <Typography sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                }}>
                                    No headphones
                                </Typography>
                            </Box>
                        </Box>
                        <Typography sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '11px',
                            fontWeight: 400,
                            color: 'var(--app-text-subtle)',
                            mt: 0.75,
                        }}>
                            This helps optimize audio recording quality
                        </Typography>
                    </Box>

                    {/* Error message */}
                    {errorMessage && !isHost && (
                        <Box sx={{
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            borderRadius: '8px',
                            p: 2,
                        }}>
                            <Typography sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '13px',
                                fontWeight: 500,
                                color: '#DC2626',
                            }}>
                                {errorMessage}
                            </Typography>
                        </Box>
                    )}

                    {/* Primary CTA */}
                    <Button
                        fullWidth
                        disabled={!canEnter}
                        onClick={handleEnterStudio}
                        sx={{
                            height: 48,
                            borderRadius: '10px',
                            backgroundColor: '#000000',
                            color: '#FFFFFF',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            mt: 1,
                            '&:hover': {
                                backgroundColor: '#1A1A1A',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                            },
                            '&:disabled': {
                                backgroundColor: '#000000',
                                color: '#FFFFFF',
                                opacity: 0.3,
                            },
                        }}
                    >
                        {isHost ? 'Enter studio' : 'Join recording'}
                    </Button>
                </Box>
                )}
            </Box>
        </Box>
    );
};

export default NewLobby;
